import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import { useNavigate } from 'react-router-dom';
import { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { QuantityPricingContext } from '../../providers/QuantityPricingContext';
import { getPricingScopeKey } from '../../utils/quantityPricing';
import { FormattedDescription } from '../../utils/formatDescription';
import { showCartToast } from '../../utils/sweetAlert';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { isCustomizableProduct } from '../../utils/isCustomizableProduct';

const loadedHoverImages = new Set();
const pendingHoverImages = new Map();

const preloadHoverImage = (url) => {
  if (!url || loadedHoverImages.has(url)) return Promise.resolve(Boolean(url));
  if (pendingHoverImages.has(url)) return pendingHoverImages.get(url);

  const request = new Promise(resolve => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      loadedHoverImages.add(url);
      pendingHoverImages.delete(url);
      resolve(true);
    };
    image.onerror = () => {
      pendingHoverImages.delete(url);
      resolve(false);
    };
    image.src = url;
  });

  pendingHoverImages.set(url, request);
  return request;
};

const withRetryParam = (url) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}imageRetry=${Date.now()}`;
};

const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getUnitPrice } = useContext(QuantityPricingContext);
  const cartItems = useSelector((state) => state.cart.items);
  const allImages = useMemo(() => {
    const productImages = (product.images || []).filter(image => typeof image === 'string' && image.trim());
    const sourceImages = productImages.length > 0
      ? productImages
      : product.image
        ? [product.image]
        : ['/placeholder-image.svg'];

    return [...new Set(sourceImages.map(getDisplayImageUrl))];
  }, [product.image, product.images]);
  const primaryImageUrl = allImages[0] || '/placeholder-image.svg';
  const hoverImageUrl = allImages[1] || '';
  const hasMultipleImages = allImages.length > 1;
  const [primaryImageSrc, setPrimaryImageSrc] = useState(primaryImageUrl);
  const [isHovering, setIsHovering] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [hoverImageReady, setHoverImageReady] = useState(() => loadedHoverImages.has(hoverImageUrl));
  const primaryRetryCount = useRef(0);
  const retryTimer = useRef(null);

  // Usar basePrice si existe, sino price legacy
  const basePrice = product.basePrice || product.price;
  
  // Calcular precio final considerando el descuento
  const discountedBasePrice = product.discount > 0 
    ? parseFloat((basePrice - basePrice * product.discount / 100).toFixed(2))
    : basePrice;

  const categoryId = product.category && typeof product.category === 'object' ? product.category._id : product.category;
  // Cantidad ya presente en el carrito de esta misma categoría (otros productos incluidos),
  // para que el precio mostrado refleje el descuento por cantidad acumulado.
  const pricingKey = getPricingScopeKey(categoryId, product.subcategory);
  const existingCategoryQtyInCart = cartItems.reduce((acc, item) => (
    getPricingScopeKey(item.category, item.subcategory) === pricingKey ? acc + item.quantity : acc
  ), 0);
  const finalPrice = getUnitPrice(categoryId, product.subcategory, existingCategoryQtyInCart + 1, discountedBasePrice);

  // Obtener precio mínimo de variantes si existen
  const getMinVariantPrice = () => {
    if (!product.hasVariants || !product.variants?.length) return finalPrice;
    
    const variantPrices = product.variants
      .filter(v => v.isActive !== false)
      .map(v => {
        const variantPrice = v.price || basePrice;
        return product.discount > 0 
          ? variantPrice - (variantPrice * product.discount / 100)
          : variantPrice;
      });
    
    return variantPrices.length > 0 ? Math.min(...variantPrices) : finalPrice;
  };

  const minPrice = getMinVariantPrice();
  const hasMultiplePrices = product.hasVariants && product.variants?.some(v => v.price && v.price !== basePrice);
  const requiresQuote = isCustomizableProduct(product);

  const handleAddToCart = () => {
    if (product.hasVariants && product.variants?.length > 0) {
      // Si tiene variantes, redirigir a la página del producto para selección
      navigate(`/product/${product._id}`);
    } else {
      // Si no tiene variantes, agregar directamente al carrito
      dispatch(addToCart({ ...product, price: finalPrice, unitBasePrice: discountedBasePrice }));
      showCartToast(product.name);
    }
  };

  const handleViewDetails = () => {
    navigate(`/product/${product._id}`);
  };

  // Función para truncar la descripción formateada (limpia HTML si existe)
  const getTruncatedDescription = (description, wordLimit = 15) => {
    if (!description) return '';
    
    // Si contiene HTML, extraer solo el texto
    let cleanText = description;
    if (description.includes('<')) {
      // Crear un elemento temporal para extraer texto del HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = description;
      cleanText = tempDiv.textContent || tempDiv.innerText || '';
    }
    
    // Formatear para manejar \n
    const formatted = cleanText.replace(/\\n/g, ' ').replace(/\r\n/g, ' ').replace(/\r/g, ' ').replace(/\s+/g, ' ').trim();
    const words = formatted.split(' ').filter(word => word.trim());
    
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(' ') + '...';
    }
    return formatted;
  };

  const truncatedDescription = getTruncatedDescription(product.description, 15);
  
  // Mantener sincronizada la imagen principal cuando cambia el producto
  useEffect(() => {
    primaryRetryCount.current = 0;
    setPrimaryImageSrc(primaryImageUrl);
    setImageLoading(true);
    return () => clearTimeout(retryTimer.current);
  }, [primaryImageUrl]);

  // Precargar la segunda imagen solamente cuando existe intención de hover
  useEffect(() => {
    setHoverImageReady(loadedHoverImages.has(hoverImageUrl));
    if (!isHovering || !hoverImageUrl || loadedHoverImages.has(hoverImageUrl)) return undefined;

    let active = true;
    preloadHoverImage(hoverImageUrl).then(loaded => {
      if (active && loaded) setHoverImageReady(true);
    });
    return () => {
      active = false;
    };
  }, [hoverImageUrl, isHovering]);

  const handlePrimaryImageError = () => {
    if (primaryImageSrc === '/placeholder-image.svg') {
      setImageLoading(false);
      return;
    }

    if (primaryRetryCount.current === 0) {
      primaryRetryCount.current = 1;
      retryTimer.current = setTimeout(() => setPrimaryImageSrc(withRetryParam(primaryImageUrl)), 500);
      return;
    }

    setPrimaryImageSrc('/placeholder-image.svg');
    setImageLoading(false);
  };

  const handleHoverImageError = () => {
    loadedHoverImages.delete(hoverImageUrl);
    setHoverImageReady(false);
  };

  return (
    <div className="product-card product-card-enhanced">
      <div 
        className="product-image-container"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Loader circular pequeño */}
        {imageLoading && (
          <div className="image-loader">
            <div className="image-spinner"></div>
          </div>
        )}
        <img
          src={primaryImageSrc}
          alt={product.name}
          className={`imagentienda product-image-primary ${imageLoading ? 'is-loading' : ''}`}
          onClick={handleViewDetails}
          loading="lazy"
          decoding="async"
          onLoad={() => setImageLoading(false)}
          onError={handlePrimaryImageError}
        />
        {hoverImageUrl && hoverImageReady && (
          <img
            src={hoverImageUrl}
            alt=""
            aria-hidden="true"
            className={`imagentienda product-image-hover ${isHovering ? 'is-visible' : ''}`}
            decoding="async"
            onError={handleHoverImageError}
          />
        )}
        {/* Indicador de múltiples imágenes */}
        {hasMultipleImages && !imageLoading && (
          <div style={{ position: 'absolute', top: '6px', right: '6px', zIndex: 2 }}>
            <span
              className="badge bg-dark"
              style={{ fontSize: '0.65em', opacity: '0.75', borderRadius: '10px' }}
            >
              {isHovering && hoverImageReady ? 2 : 1}/{allImages.length}
            </span>
          </div>
        )}
      </div>
      
      <div className="product-card-info">
        <h3 className="product-card-name" onClick={handleViewDetails} style={{ cursor: 'pointer' }}>
          {product.name}
        </h3>
        <p className="product-card-desc">{truncatedDescription}</p>
        
        {/* Mostrar información de variantes */}
        {product.hasVariants && product.variants?.length > 0 && (
          <span className="product-card-variants">
            {product.variants.length} variante{product.variants.length > 1 ? 's' : ''}
          </span>
        )}
        
        {/* Los pedidos personalizados se cotizan según cantidad y estampados. */}
        <div className="product-card-price">
          {requiresQuote ? (
            <span className="quote-request-label">
              <i className="fa-solid fa-comments me-1"></i>
              Cotización a medida
            </span>
          ) : product.discount > 0 ? (
            <>
              <span className="price-original"><del>${basePrice}</del></span>
              <span className="price-current">
                {hasMultiplePrices ? `Desde $${minPrice.toFixed(2)}` : `$${finalPrice}`}
              </span>
            </>
          ) : (
            <span className="price-current">
              {hasMultiplePrices ? `Desde $${minPrice.toFixed(2)}` : `$${basePrice}`}
            </span>
          )}
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '0.3rem' }}>
          {requiresQuote ? (
            <button onClick={handleViewDetails} className="custom-btn btn-with-text">
              <i className="fa-solid fa-comments"></i>
              <span>Consultar</span>
            </button>
          ) : product.hasVariants ? (
            <button onClick={handleAddToCart} className="custom-btn btn-with-text">
              <i className="fa-solid fa-eye"></i>
              <span>Ver opciones</span>
            </button>
          ) : (
            <>
              <button onClick={handleAddToCart} className="custom-btn" aria-label={`Agregar ${product.name} al carrito`}>
                <i className="fa-solid fa-cart-shopping" aria-hidden="true"></i>
              </button>
              <button onClick={handleViewDetails} className="custom-btn" aria-label={`Ver ${product.name}`}>
                <i className="fa-solid fa-eye" aria-hidden="true"></i>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
